import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { MinusIcon, PlusIcon, CheckIcon } from "@heroicons/react/24/solid";
import { toast } from "react-hot-toast";
import Input from "shared/Input/Input";
import { INcInputNumber } from "Interfaces/interface.uiUtils";

const NcInputNumber: FC<INcInputNumber> = ({
  className = "",
  defaultValue = 25,
  widthClass = "w-[150px]",
  min = 25,
  max = 50000,
  id,
  productId,
  size = "md",
  fullWidth = false,
  onChange,
  label,
  desc,
  disabled,
  onQuantityChanging,
  onInputFocusChange,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [inputValue, setInputValue] = useState(defaultValue.toString());
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
    setInputValue(defaultValue.toString());
  }, [defaultValue]);

  useLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = value.toString();
      setInputValue(value.toString());
    }
  }, [value]);

  const changeValue = (newValue: number) => {
    setIsChanging(true);
    onQuantityChanging?.(true);

    if (isNaN(newValue)) {
      toast.error("Only numbers are allowed");
      setValue(min);
      setInputValue(min.toString());
      return;
    }

    if (newValue < min) {
      toast.error(`Minimum quantity is ${min}`);
      setInputValue(value.toString());
      return;
    }
    if (max && newValue > max) {
      toast.error(`Maximum quantity is ${max}`);
      setInputValue(value.toString());
      return;
    }

    setValue(newValue);
    setInputValue(newValue.toString());
    onChange?.(newValue);

    setTimeout(() => {
      setIsChanging(false);
      onQuantityChanging?.(false);
    }, 300);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const numericValue = rawValue.replace(/[^0-9]/g, "");
    setInputValue(numericValue);
    if (inputRef.current) {
      inputRef.current.value = numericValue;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "Backspace",
      "Delete",
      "Tab",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
    ];
    if (!allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleCheck = () => {
    const raw = inputRef.current?.value;
    const parsed = raw ? parseInt(raw) : NaN;
    if (!raw || isNaN(parsed)) {
      toast.error("Please enter a valid number");
      setInputValue(value.toString());

      setIsFocused(false);
      onInputFocusChange?.(false);
      return;
    }
    changeValue(parsed);
    setIsFocused(false);
    onInputFocusChange?.(false);
  };

  const sizeClasses = {
    sm: "h-8 text-sm",
    md: "h-10 text-base",
    lg: "h-12 text-lg",
  };

  const iconSize =
    size === "sm" ? "w-4 h-4" : size === "lg" ? "w-5 h-5" : "w-4.5 h-4.5";

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {(label || desc) && (
        <div className="mb-1">
          {label && (
            <span className="block font-medium text-neutral-800 dark:text-white">
              {label}
            </span>
          )}
          {desc && (
            <span className="block text-sm text-neutral-500 dark:text-neutral-400">
              {desc}
            </span>
          )}
        </div>
      )}

      <div
        className={`flex items-center gap-1 md:gap-2 border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 rounded-lg px-2 shadow-sm transition-all duration-150 ${
          sizeClasses[size]
        } ${fullWidth ? "w-full" : widthClass} ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {!isFocused && (
          <button
            onClick={() => changeValue(value - 1)}
            className={`focus:outline-none transition-opacity duration-150 ${
              disabled
                ? "cursor-not-allowed"
                : value <= min
                ? "opacity-30 cursor-not-allowed"
                : "hover:opacity-70"
            }`}
            disabled={value <= min || disabled}
          >
            <MinusIcon className={`${iconSize}`} />
          </button>
        )}

        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (!disabled) {
              setIsFocused(true);
              onInputFocusChange?.(true);
            }
          }}
          onBlur={() => {
            if (!inputValue || Number(inputValue) < min) {
              setInputValue(min.toString());
            }
          }}
          className={`bg-transparent border-none w-full focus:outline-none focus:ring-0 ${
            disabled
              ? "pointer-events-none bg-transparent cursor-not-allowed text-neutral-400"
              : "text-neutral-900 dark:text-white"
          } ${isFocused ? "text-left" : "text-center"}`}
          placeholder={min.toString()}
        />

        {isFocused ? (
          <button
            onClick={handleCheck}
            className={`transition-opacity duration-150 ${
              disabled ? "cursor-not-allowed opacity-30" : "hover:opacity-70"
            }`}
            disabled={disabled}
          >
            <CheckIcon className={`${iconSize}`} />
          </button>
        ) : (
          <button
            onClick={() => changeValue(value + 1)}
            disabled={value >= max || disabled}
            className={`transition-opacity duration-150 ${
              disabled
                ? "cursor-not-allowed"
                : value >= max
                ? "opacity-30 cursor-not-allowed"
                : "hover:opacity-70"
            }`}
          >
            <PlusIcon className={`${iconSize}`} />
          </button>
        )}
      </div>
    </div>
  );
};

export default NcInputNumber;
